/**
 * Tiny URL-state helpers for the coordinate query params.
 *
 * The homepage uses `?lat=…&lon=…` for shareable deep-links:
 * - On mount, HeroMap reads the URL and centers there (falls back to the
 *   Tucson demo coord when missing / invalid).
 * - After a resolve, the URL is rewritten via `history.replaceState` so
 *   there's no scroll/navigation event but the address bar stays correct.
 *
 * Pure functions only — easy to test, no React.
 */

import { normalizeCoord } from '@/lib/geo'

const LAT_KEY = 'lat'
const LON_KEY = 'lon'
const ZOOM_KEY = 'z'

/**
 * Parse `?lat=…&lon=…` out of an arbitrary URL-search string. Returns
 * `[lon, lat]` (MapLibre / GeoJSON order) or `null` if either coordinate
 * is missing or non-numeric. Out-of-range longitudes are wrapped to
 * [-180, 180] and out-of-range latitudes clamped to [-90, 90] via
 * normalizeCoord — that way shareable links that crossed the antimeridian
 * still resolve to the intended point instead of dropping you back on the
 * default demo coordinate.
 */
export function readCoordFromSearch(search: string): [number, number] | null {
  const params = new URLSearchParams(search)
  const latStr = params.get(LAT_KEY)
  const lonStr = params.get(LON_KEY)
  if (latStr == null || lonStr == null) return null
  const lat = Number.parseFloat(latStr)
  const lon = Number.parseFloat(lonStr)
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null
  return normalizeCoord(lon, lat)
}

/** Parse `?z=…` (map zoom) out of a search string, or null when absent/invalid. */
export function readZoomFromSearch(search: string): number | null {
  const z = new URLSearchParams(search).get(ZOOM_KEY)
  if (z == null) return null
  const n = Number.parseFloat(z)
  if (!Number.isFinite(n) || n < 0 || n > 24) return null
  return n
}

/**
 * Return a new search string with `lat` and `lon` set (4-decimal precision),
 * and `z` (2-decimal) when a zoom is supplied. Other existing params — and the
 * existing `z` when zoom is omitted — are preserved.
 */
export function writeCoordToSearch(
  search: string,
  lon: number,
  lat: number,
  zoom?: number,
): string {
  const params = new URLSearchParams(search)
  params.set(LAT_KEY, lat.toFixed(4))
  params.set(LON_KEY, lon.toFixed(4))
  if (zoom != null && Number.isFinite(zoom)) params.set(ZOOM_KEY, zoom.toFixed(2))
  return params.toString()
}

/**
 * Extract just the coordinate params (`lat`, `lon`, `z`) from a search string,
 * returning a `?lat=…&lon=…&z=…` string (with leading `?`) or `''` when none
 * are present. Used to carry the current map view across nav links (logo →
 * home, Explore) so switching pages keeps the same location + zoom.
 */
export function pickCoordParams(search: string): string {
  const src = new URLSearchParams(search)
  const out = new URLSearchParams()
  for (const key of [LAT_KEY, LON_KEY, ZOOM_KEY]) {
    const v = src.get(key)
    if (v != null) out.set(key, v)
  }
  const s = out.toString()
  return s ? `?${s}` : ''
}

/** Browser-side: read from window.location.search. */
export function readCoordFromUrl(): [number, number] | null {
  if (typeof window === 'undefined') return null
  return readCoordFromSearch(window.location.search)
}

/** Browser-side: read the `z` (zoom) param, or null when absent/invalid. */
export function readZoomFromUrl(): number | null {
  if (typeof window === 'undefined') return null
  return readZoomFromSearch(window.location.search)
}

/**
 * Browser-side: rewrite the address bar with the new coord. Uses
 * `history.replaceState` so the browser does not record a history entry
 * for every pin drag — drag → resolve fires many of these.
 */
export function writeCoordToUrl(lon: number, lat: number, zoom?: number): void {
  if (typeof window === 'undefined') return
  const next = writeCoordToSearch(window.location.search, lon, lat, zoom)
  const url = `${window.location.pathname}?${next}${window.location.hash}`
  window.history.replaceState(null, '', url)
}
